// src/routes/system/cities/Cities.tsx
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import cityStore from '../../../store/cities.store';
import type { ICity } from '../../../api/cities';

const Cities = () => {
  const [confirmCity, setConfirmCity] = useState<ICity | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    cityStore.loadList();
  }, []);

  const handleDelete = async () => {
    if (!confirmCity) return;
    try {
      await cityStore.remove(confirmCity.id);
      setToast('Город удалён');
    } catch {
      // ошибка уже в cityStore.lastError
    } finally {
      setConfirmCity(null);
    }
  };

  // ---- вместо вложенных тернарников — явная сборка контента ----
  let content: React.ReactNode;

  if (cityStore.loading) {
    content = <CircularProgress />;
  } else if (!cityStore.list.length) {
    content = (
      <Typography color="text.secondary">Пока нет ни одного города.</Typography>
    );
  } else {
    content = (
      <List>
        {cityStore.list.map((c: ICity) => {
          const deleting = cityStore.removingIds?.has?.(Number(c.id)) || false;

          return (
            <ListItem
              key={c.id}
              disablePadding
              secondaryAction={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title="Редактировать">
                    <IconButton
                      component={Link}
                      to={`/cities/${c.id}`}
                      size="small"
                      color="primary"
                      edge="end"
                      aria-label="edit"
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Удалить">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        edge="end"
                        aria-label="delete"
                        onClick={() => setConfirmCity(c)}
                        disabled={deleting}
                      >
                        {deleting ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteOutlineIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemButton component={Link} to={`/cities/${c.id}`}>
                <ListItemText primary={c.name} secondary={c.subdomain} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  }

  return (
    <Box p={3} width="100%">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        mb={3}
        width="100%"
      >
        <Typography variant="h5">Города</Typography>
        <Button component={Link} to="/cities/new" variant="contained">
          Добавить город
        </Button>
      </Box>

      {content}

      {/* Диалог подтверждения удаления */}
      <Dialog open={!!confirmCity} onClose={() => setConfirmCity(null)}>
        <DialogTitle>Удалить город?</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmCity
              ? `Вы действительно хотите удалить «${confirmCity.name}» (${confirmCity.subdomain})?`
              : 'Подтвердите удаление.'}
          </Typography>

          {cityStore.lastError && (
            <Alert sx={{ mt: 2 }} severity="error">
              {cityStore.lastError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCity(null)}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={
              confirmCity
                ? cityStore.removingIds?.has?.(Number(confirmCity.id))
                : false
            }
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Тост об успехе */}
      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity="success"
          variant="filled"
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default observer(Cities);
